import { TextField, PasswordField, Button, Flex } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import React, { useCallback, useState } from 'react';

import logo from 'assets/images/planera-logo.png';
import { showSuccessToast, showErrorToast } from 'components/Toast/Toast';
import { EventKey } from 'helpers/keyboardShortcuts';
import { validatePassword } from 'helpers/passwordValidation';
import { updateUserPartial, updateUserPassword } from 'state_management/accounts/users/api';
import { NEED_PASSWORD_SETUP_ATTRIBUTE_KEY } from 'state_management/threadedConstants';

import { handleAcceptInvite } from '../auth-utils';
import { magicLinkSetupWrapStyles, magicLinkSetupStyles, passwordFieldStyles } from './MagicLinkUserSetup.styles';

const ERROR_MESSAGES = {
  NAME_REQUIRED: 'Name is required',
  PASSWORD_REQUIRED: 'Password is required',
  CONFIRM_PASSWORD_REQUIRED: 'Please confirm your password',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match',
  SETUP_FAILED: 'Failed to complete account setup. Please try again.'
} as const;

type SetupFormData = {
  name: string;
  password: string;
  confirmPassword: string;
};

type ValidationErrors = {
  name?: string;
  password?: string;
  confirmPassword?: string;
};

function MagicLinkUserSetup() {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<SetupFormData>({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const handleInputChange = useCallback(
    (field: keyof SetupFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when the user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ERROR_MESSAGES.NAME_REQUIRED;
    }

    if (!formData.password) {
      newErrors.password = ERROR_MESSAGES.PASSWORD_REQUIRED;
    } else {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        newErrors.password = passwordErrors.join('\n');
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.confirmPassword, formData.name, formData.password]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const currentUserId = currentUser.attributes.sub ?? '';
      if (!currentUserId) {
        showErrorToast('Unable to retrieve user information. Please try again.');
        return;
      }

      // Update user password
      await updateUserPassword(currentUserId, formData.password);

      // Update user name
      await updateUserPartial(currentUserId, {
        name: formData.name,
        email: currentUser.attributes.email ?? ''
      });

      // Update the custom attribute used for magic link setup otherwise we would need to sign in again
      await Auth.updateUserAttributes(currentUser, { [NEED_PASSWORD_SETUP_ATTRIBUTE_KEY]: 'true' });

      showSuccessToast('Account setup completed successfully!');

      await handleAcceptInvite(currentUserId);

      window.location.reload();
    } catch (error) {
      logger.error('Error setting up account:', {
        error,
        rum: {
          context: {
            feature: 'magic-link-user-setup',
            action: 'submit-account-setup'
          }
        }
      });
      showErrorToast(ERROR_MESSAGES.SETUP_FAILED);
    } finally {
      setIsLoading(false);
    }
  }, [formData.name, formData.password, isLoading, validateForm]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === EventKey.ENTER) {
        event.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className={magicLinkSetupWrapStyles}>
      <div
        data-amplify-container=""
        className={`magicLinkSetup ${magicLinkSetupStyles}`}>
        <div className="logo-wrap">
          <img
            className="logo"
            alt="Planera logo"
            src={logo}
          />
          <div className="logoTitle">Complete your account setup</div>
        </div>
        <div
          data-amplify-router=""
          style={{ padding: '2rem' }}>
          <fieldset
            className="amplify-flex loginInputFieldWrapper"
            style={{ flexDirection: 'column' }}>
            <Flex
              direction="column"
              gap="1rem">
              {/* Name Field */}
              <TextField
                label="Name"
                placeholder="Your Name"
                value={formData.name}
                hasError={!!errors.name}
                errorMessage={errors.name}
                isRequired
                onKeyDown={handleKeyDown}
                onChange={(event) => handleInputChange('name', event.target.value)}
              />

              {/* Password Field */}
              <PasswordField
                label="Password"
                placeholder="Set New Password"
                value={formData.password}
                hasError={!!errors.password}
                errorMessage={errors.password}
                isRequired
                autoComplete="new-password"
                onKeyDown={handleKeyDown}
                className={passwordFieldStyles}
                onChange={(event) => handleInputChange('password', event.target.value)}
              />

              {/* Confirm Password Field */}
              <PasswordField
                label="Confirm Password"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                hasError={!!errors.confirmPassword}
                errorMessage={errors.confirmPassword}
                isRequired
                autoComplete="new-password"
                onKeyDown={handleKeyDown}
                className={passwordFieldStyles}
                onChange={(event) => handleInputChange('confirmPassword', event.target.value)}
              />
            </Flex>

            <div
              className="amplify-flex"
              style={{ flexDirection: 'column', marginTop: '1rem' }}>
              <Button
                variation="primary"
                onClick={handleSubmit}
                isLoading={isLoading}
                loadingText="Setting up..."
                style={{ fontWeight: 'var(--amplify-font-weights-normal)' }}>
                Done
              </Button>
            </div>
          </fieldset>
        </div>

        <div className="footer-wrap">
          <p>&copy; {new Date().getFullYear()} Planera.io All Rights Reserved</p>
        </div>
      </div>
    </div>
  );
}

export default MagicLinkUserSetup;
